import type { CoverageApi } from '../api/client';
import {
  openAnalyticsPrintReport,
  openPersonUnfinishedMapsPrint,
  type AnalyticsPrintReport
} from './print-report';
import {
  mountAdminCoverageMap,
  type AdminCoverageMapHandle
} from '../admin/coverage-map';
import {
  COVERAGE_DATA_CHANGED_EVENT,
  COVERAGE_DATA_REVISION_KEY
} from '../data-sync';
import type {
  AdminCoverageDashboard,
  AdminCoverageMetrics,
  AdminCoverageSection,
  SeasonState,
  StoredIdentitySession
} from '../domain/types';
import { auditCompletedSectionCredits, sectionDoorCreditForPerson } from './door-credit';

type AnalyticsView = 'CHURCH' | 'NEIGHBORHOOD' | 'GROUP' | 'PERSON' | 'COMMENTS';

type AnalyticsEntity = {
  id: string;
  label: string;
  subtitle: string;
  sectionIds: string[];
  assignedSectionIds?: string[];
  groupId?: string;
};

type NeighborhoodSignal = {
  neighborhoodId: string;
  neighborhoodName: string;
  publishedDoors: number;
  observedHouses: number;
  noSoliciting: number;
  emptyLots: number;
  underConstruction: number;
};

type AnalyticsModel = {
  dashboard: AdminCoverageDashboard;
  neighborhoods: AnalyticsEntity[];
  groups: AnalyticsEntity[];
  people: AnalyticsEntity[];
};

type AnalyticsState = {
  view: AnalyticsView;
  selectedId: string;
  query: string;
};

type TopTenEntity = {
  entity: AnalyticsEntity;
  completedDoors: number;
  completedSections: number;
  totalDoors: number;
  completionPercent: number;
  participants: number;
};

const VIEW_LABELS: Record<AnalyticsView, string> = {
  CHURCH: 'Whole church',
  NEIGHBORHOOD: 'Neighborhoods',
  GROUP: 'Small groups',
  PERSON: 'People',
  COMMENTS: 'Comments'
};

export function startAnalyticsApp(
  root: HTMLDivElement,
  api: CoverageApi,
  identitySession: StoredIdentitySession,
  signOut: () => void
): void {
  root.innerHTML = analyticsShellTemplate(
    identitySession,
    '<section class="analytics-loading-card"><div class="loading-spinner" aria-hidden="true"></div><div><strong>Loading live coverage…</strong><span>Reading the same assignments and published maps used by the volunteer, leader, and admin pages.</span></div></section>'
  );
  wireHeaderSignOut(root, signOut);

  const state: AnalyticsState = { view: 'CHURCH', selectedId: '', query: '' };
  let model: AnalyticsModel | null = null;
  let season: SeasonState | null = null;
  let mapHandle: AdminCoverageMapHandle | null = null;
  let lastSyncedAt = '';
  let refreshInFlight = false;

  const render = (): void => {
    if (!model || !season) return;
    mapHandle?.destroy();
    mapHandle = null;

    const selectedEntity = findSelectedEntity(model, state);
    const scopedSections = sectionsForView(model.dashboard.sections, state.view, selectedEntity);
    const metrics = state.view === 'PERSON' && selectedEntity
      ? personCoverageMetrics(scopedSections, selectedEntity)
      : coverageMetrics(scopedSections);
    const signals = neighborhoodSignals(model.dashboard.sections);
    const selectedSignal = state.view === 'NEIGHBORHOOD' && selectedEntity
      ? signals.find((item) => item.neighborhoodId === selectedEntity.id)
      : undefined;

    root.innerHTML = analyticsShellTemplate(
      identitySession,
      analyticsContentTemplate(model, state, metrics, selectedEntity, signals, selectedSignal, season, lastSyncedAt)
    );

    wireAnalyticsPage(root, state, render, signOut, {
      printReport: () => openAnalyticsPrintReport(buildAnalyticsPrintReport(model!, season!)),
      printPersonMaps: () => {
        if (!selectedEntity || state.view !== 'PERSON') return;
        const unfinished = unfinishedAssignedSectionsForPerson(model!, selectedEntity);
        if (!unfinished.length) return;
        openPersonUnfinishedMapsPrint(selectedEntity.label, unfinished, model!.dashboard.sections);
      },
      refresh: () => void refresh(true)
    });

    const mapElement = root.querySelector<HTMLDivElement>('#analytics-decision-map');
    if (mapElement && scopedSections.length) {
      const mapDashboard: AdminCoverageDashboard = {
        scope: 'MASTER',
        metrics,
        sections: scopedSections,
        updatedAt: model.dashboard.updatedAt
      };
      mapHandle = mountAdminCoverageMap('analytics-decision-map', mapDashboard);
    }
  };

  const refresh = async (showLoading: boolean): Promise<void> => {
    if (refreshInFlight) return;
    refreshInFlight = true;
    const button = root.querySelector<HTMLButtonElement>('#analytics-refresh');
    if (button) {
      button.disabled = true;
      button.textContent = 'Refreshing…';
    }
    if (showLoading && !model) {
      root.innerHTML = analyticsShellTemplate(
        identitySession,
        '<section class="analytics-loading-card"><div class="loading-spinner" aria-hidden="true"></div><div><strong>Refreshing live data…</strong><span>Syncing assignments, completions, team members, and published sections.</span></div></section>'
      );
      wireHeaderSignOut(root, signOut);
    }
    try {
      const [dashboard, currentSeason] = await Promise.all([
        api.getAdminCoverage(identitySession.sessionToken),
        api.getSeason(identitySession.sessionToken)
      ]);
      model = buildAnalyticsModel(dashboard);
      season = currentSeason;
      lastSyncedAt = new Date().toISOString();

      if (state.selectedId && !findSelectedEntity(model, state)) {
        state.selectedId = '';
        state.query = '';
      }
      render();
    } catch (error) {
      if (!model) {
        const message = error instanceof Error ? error.message : 'The published coverage map could not be loaded.';
        root.innerHTML = analyticsShellTemplate(
          identitySession,
          `<section class="analytics-loading-card error"><div><strong>Analytics could not open</strong><span>${escapeHtml(message)}</span><button id="analytics-retry" class="primary-button" type="button">Try again</button></div></section>`
        );
        wireHeaderSignOut(root, signOut);
        root.querySelector<HTMLButtonElement>('#analytics-retry')?.addEventListener('click', () => void refresh(true));
      } else {
        render();
      }
    } finally {
      refreshInFlight = false;
    }
  };

  const refreshWhenVisible = (): void => {
    if (document.visibilityState === 'visible') void refresh(false);
  };
  const refreshFromStorage = (event: StorageEvent): void => {
    if (event.key === COVERAGE_DATA_REVISION_KEY) void refresh(false);
  };
  const refreshFromCustomEvent = (): void => { void refresh(false); };

  window.addEventListener('focus', refreshWhenVisible);
  document.addEventListener('visibilitychange', refreshWhenVisible);
  window.addEventListener('storage', refreshFromStorage);
  window.addEventListener(COVERAGE_DATA_CHANGED_EVENT, refreshFromCustomEvent);
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void refresh(false);
  }, 30000);

  void refresh(true);
}

function analyticsShellTemplate(identitySession: StoredIdentitySession, content: string): string {
  return `
    <div class="app-shell analytics-shell">
      <header class="app-header analytics-header">
        <a class="brand-block brand-link" href="/analytics">
          <div class="brand-mark" aria-hidden="true">B</div>
          <div><div class="brand-title">Buckeye Coverage Analytics</div><div class="brand-subtitle">Live admin decision center</div></div>
        </a>
        <div class="analytics-header-actions">
          <span class="analytics-signed-in">${escapeHtml(identitySession.person.displayName)}</span>
          <details class="access-switcher">
            <summary>View as</summary>
            <div class="access-switcher-menu">
              <a href="/analytics">Analytics</a>
              <a href="/admin">Admin</a>
              ${identitySession.person.roles.includes('LEADER') ? '<a href="/leader">Leader</a>' : ''}
              <a href="/">Volunteer</a>
              <button data-refresh-app type="button">Refresh app</button><button data-analytics-sign-out type="button">Sign out</button>
            </div>
          </details>
        </div>
      </header>
      <main class="analytics-main">${content}</main>
    </div>
  `;
}

function analyticsContentTemplate(
  model: AnalyticsModel,
  state: AnalyticsState,
  metrics: AdminCoverageMetrics,
  selectedEntity: AnalyticsEntity | undefined,
  signals: NeighborhoodSignal[],
  selectedSignal: NeighborhoodSignal | undefined,
  season: SeasonState,
  lastSyncedAt: string
): string {
  const currentViewLabel = VIEW_LABELS[state.view];
  const displayTitle = selectedEntity?.label ?? currentViewLabel;
  const mapReady = state.view === 'CHURCH' || Boolean(selectedEntity);
  const statusLabel = season.status === 'OPEN' ? 'Open' : season.status === 'PAUSED' ? 'Paused' : 'Closed';

  return `
    <section class="analytics-page analytics-decision-page">
      <div class="analytics-hero decision-hero">
        <div>
          <p class="eyebrow">Admin only · live data</p>
          <h1>Analytics</h1>
          <p>This page reads the same published sections, assignments, completions, team members, and house reports used everywhere else in Buckeye Coverage.</p>
        </div>
        <div class="demo-data-badge decision-demo-badge analytics-live-badge">
          <strong>${escapeHtml(season.name)} · ${statusLabel}</strong>
          <span>Last refreshed ${escapeHtml(formatRelativeSync(lastSyncedAt))}</span>
        </div>
      </div>

      <section class="decision-toolbar analytics-card" aria-label="Analytics controls">
        <div class="decision-season-field analytics-current-season">
          <span>Current season</span>
          <strong>${escapeHtml(season.name)}</strong>
        </div>
        <div class="decision-view-tabs" role="tablist" aria-label="Analytics view">
          ${(['CHURCH', 'NEIGHBORHOOD', 'GROUP', 'PERSON', 'COMMENTS'] as AnalyticsView[]).map((view) => `<button type="button" role="tab" class="decision-view-tab ${state.view === view ? 'active' : ''}" aria-selected="${state.view === view}" data-analytics-view="${view}">${VIEW_LABELS[view]}</button>`).join('')}
        </div>
        <div class="analytics-toolbar-actions">
          <button id="analytics-refresh" type="button" class="secondary-button analytics-refresh-button">Refresh</button>
          <button id="analytics-print-report" type="button" class="secondary-button analytics-print-report-button">Print PDF report</button>
        </div>
      </section>

      ${state.view === 'COMMENTS' ? commentsTemplate(model.dashboard.sections) : ''}

      ${state.view !== 'COMMENTS' && state.view !== 'CHURCH' ? entitySearchTemplate(model, state, selectedEntity) : ''}

      ${state.view !== 'COMMENTS'
        ? mapReady
          ? metricGridTemplate(state.view, metrics, selectedEntity, selectedSignal, model)
          : searchWelcomeTemplate(state.view)
        : ''}

      ${state.view !== 'COMMENTS' && mapReady ? needsAttentionTemplate(state.view, metrics, selectedEntity, selectedSignal, model) : ''}

      ${state.view !== 'COMMENTS' && mapReady ? `
        <section class="decision-content-grid">
          <article class="analytics-card decision-map-card">
            <div class="analytics-card-heading decision-map-heading">
              <div><p class="eyebrow">Live published section map</p><h2>${escapeHtml(displayTitle)}</h2></div>
              <div class="analytics-map-legend"><span><i class="legend-dot coverage-unassigned"></i>Not assigned</span><span><i class="legend-dot coverage-assigned"></i>Assigned</span><span><i class="legend-dot coverage-complete"></i>Completed</span></div>
            </div>
            ${metrics.totalSections ? '<div id="analytics-decision-map" class="analytics-decision-map" aria-label="Published section analytics map"></div>' : '<div class="analytics-map-empty">No live sections are connected to this selection.</div>'}
            <p class="analytics-map-note">This map is live. Assignment, completion, group-reservation, and team-member changes appear after refresh and automatically when another Buckeye Coverage page changes data.</p>
          </article>
          ${decisionPanelTemplate(state.view, metrics, selectedEntity, selectedSignal, signals, model)}
        </section>
      ` : ''}
    </section>
  `;
}

function commentsTemplate(sections: AdminCoverageSection[]): string {
  const comments = sections
    .filter((section) =>
      section.status === 'COMPLETE' &&
      Boolean(section.completionComment?.trim())
    )
    .slice()
    .sort((left, right) => {
      const rightTime = right.completedAt ? Date.parse(right.completedAt) : 0;
      const leftTime = left.completedAt ? Date.parse(left.completedAt) : 0;
      return rightTime - leftTime;
    });

  return `
    <section class="analytics-card decision-search-card">
      <div class="decision-section-heading">
        <div>
          <p class="eyebrow">Completed map feedback</p>
          <h2>Volunteer comments</h2>
        </div>
        <span>${comments.length.toLocaleString()} comment${comments.length === 1 ? '' : 's'}</span>
      </div>

      ${comments.length
        ? `<div class="decision-content-grid">
            ${comments.map((section) => {
              const person =
                section.assignedTo?.displayName ??
                section.teamMembers[0]?.name ??
                'Volunteer not recorded';

              const sectionLabel = section.sectionNumber
                ? `Section ${section.sectionNumber}`
                : section.id;

              return `
                <article class="analytics-card">
                  <div class="analytics-card-heading">
                    <div>
                      <p class="eyebrow">${escapeHtml(person)}</p>
                      <h2>${escapeHtml(section.neighborhoodName)} · ${escapeHtml(sectionLabel)}</h2>
                    </div>
                    <span>${section.completedAt ? escapeHtml(formatDate(section.completedAt)) : '—'}</span>
                  </div>

                  <p><strong>Map:</strong> ${escapeHtml(section.id)}</p>
                  <p>${escapeHtml(section.completionComment?.trim() ?? '')}</p>
                </article>
              `;
            }).join('')}
          </div>`
        : `<div class="decision-empty-state">
            <strong>No completed-map comments yet.</strong>
            <p>Comments entered when volunteers finish a map will appear here automatically after refresh.</p>
          </div>`}
    </section>
  `;
}

function entitySearchTemplate(
  model: AnalyticsModel,
  state: AnalyticsState,
  selectedEntity: AnalyticsEntity | undefined
): string {
  const label = state.view === 'NEIGHBORHOOD' ? 'neighborhood' : state.view === 'GROUP' ? 'small group' : 'person';
  const results = state.query ? searchEntities(model, state.view, state.query) : [];
  const topTen = topTenEntities(model, state.view);
  const unfinishedMaps = state.view === 'PERSON' && selectedEntity
    ? unfinishedAssignedSectionsForPerson(model, selectedEntity)
    : [];

  return `
    <section class="analytics-card decision-search-card">
      <label for="analytics-entity-search">Search a ${label}</label>
      <div class="decision-search-row">
        <input id="analytics-entity-search" type="search" autocomplete="off" placeholder="Start typing a ${label} name" value="${escapeHtml(state.query)}" />
        ${selectedEntity ? `<button id="analytics-clear-entity" type="button" class="secondary-button compact-button">Clear</button>` : ''}
      </div>
      <div id="analytics-entity-results" class="decision-search-results ${state.query ? '' : 'hidden'}">
        ${state.query
          ? results.length
            ? results.map((item) => `<button type="button" data-analytics-entity-id="${escapeHtml(item.id)}"><strong>${escapeHtml(item.label)}</strong><span>${escapeHtml(item.subtitle)}</span></button>`).join('')
            : '<div class="analytics-no-results">No matching live result.</div>'
          : ''}
      </div>
      ${selectedEntity ? `
        <div class="decision-selected-entity analytics-selected-entity-row">
          <div><span>Showing</span><strong>${escapeHtml(selectedEntity.label)}</strong></div>
          ${state.view === 'PERSON' ? `<div class="analytics-selected-entity-actions">${unfinishedMaps.length
            ? `<button id="analytics-print-person-maps" type="button" class="primary-button compact-button">Print maps (${unfinishedMaps.length})</button>`
            : '<span class="analytics-no-open-maps">No unfinished assigned maps</span>'}</div>` : ''}
        </div>
      ` : '<p class="decision-search-help">Search by name, or choose from the live Top 10 below.</p>'}
      ${topTenTemplate(state.view, topTen)}
    </section>
  `;
}

function topTenEntities(model: AnalyticsModel, view: AnalyticsView): TopTenEntity[] {
  if (view === 'CHURCH') return [];
  const sections = model.dashboard.sections;
  return entitiesForView(model, view)
    .map((entity) => {
      const ids = new Set(entity.sectionIds);
      const scoped = sections.filter((section) => ids.has(section.id));
      const metrics = view === 'PERSON' ? personCoverageMetrics(scoped, entity) : coverageMetrics(scoped);
      return {
        entity,
        completedDoors: metrics.completedDoors,
        completedSections: metrics.completedSections,
        totalDoors: metrics.totalDoors,
        completionPercent: metrics.completionPercent,
        participants: participantNamesForSections(scoped).length
      };
    })
    .filter((item) => item.totalDoors > 0)
    .sort((left, right) =>
      right.completedDoors - left.completedDoors ||
      right.completedSections - left.completedSections ||
      right.completionPercent - left.completionPercent ||
      left.entity.label.localeCompare(right.entity.label)
    )
    .slice(0, 10);
}

function topTenTemplate(view: AnalyticsView, items: TopTenEntity[]): string {
  const title = view === 'NEIGHBORHOOD'
    ? 'Top 10 neighborhoods'
    : view === 'GROUP'
      ? 'Top 10 small groups'
      : 'Top 10 people';
  const empty = view === 'NEIGHBORHOOD'
    ? 'No neighborhood activity is available yet.'
    : view === 'GROUP'
      ? 'No small-group activity is available yet.'
      : 'No participant activity is available yet.';
  return `
    <div class="analytics-top-ten">
      <div class="analytics-top-ten-heading">
        <div><span>Current season leaders</span><strong>${title}</strong></div>
        <small>Ranked by doors completed</small>
      </div>
      <div class="analytics-top-ten-list">
        ${items.length
          ? items.map((item, index) => {
              const detail = view === 'GROUP'
                ? `${item.completedDoors.toLocaleString()} doors · ${item.participants.toLocaleString()} people`
                : view === 'PERSON'
                ? `${formatDoorCredits(item.completedDoors)} door-equivalents · ${item.completedSections.toLocaleString()} sections`
                  : `${item.completedDoors.toLocaleString()} of ${item.totalDoors.toLocaleString()} doors · ${item.completionPercent.toFixed(1)}% complete`;
              return `<button type="button" class="analytics-top-ten-row" data-analytics-entity-id="${escapeHtml(item.entity.id)}"><span class="analytics-top-ten-rank">${index + 1}</span><span class="analytics-top-ten-name"><strong>${escapeHtml(item.entity.label)}</strong><small>${escapeHtml(detail)}</small></span><span class="analytics-top-ten-chevron" aria-hidden="true">›</span></button>`;
            }).join('')
          : `<p class="analytics-top-ten-empty">${escapeHtml(empty)}</p>`}
      </div>
    </div>
  `;
}

function searchWelcomeTemplate(view: AnalyticsView): string {
  const subject = view === 'NEIGHBORHOOD' ? 'a neighborhood' : view === 'GROUP' ? 'a small group' : 'a person';
  const explanation = view === 'NEIGHBORHOOD'
    ? 'See live progress and real field-reported access conditions.'
    : view === 'GROUP'
      ? 'See reserved territory, maps handed out, completion, participation, and follow-up needs.'
      : 'See participation, primary assignments, unfinished maps, and completed work.';
  return `
    <section class="analytics-card decision-empty-state">
      <div class="decision-empty-icon" aria-hidden="true">⌕</div>
      <div><h2>Search ${subject}</h2><p>${escapeHtml(explanation)}</p></div>
    </section>
  `;
}

function metricGridTemplate(
  view: AnalyticsView,
  metrics: AdminCoverageMetrics,
  selectedEntity: AnalyticsEntity | undefined,
  selectedSignal: NeighborhoodSignal | undefined,
  model: AnalyticsModel
): string {
  const completion = metrics.totalDoors ? metrics.completedDoors / metrics.totalDoors * 100 : 0;
  if (view === 'CHURCH') {
    const reserved = groupReservedSections(model.dashboard.sections);
    const withPeople = model.dashboard.sections.filter((section) => section.status === 'ASSIGNED' && Boolean(section.assignedTo));
    return `<section class="decision-metric-grid" aria-label="Whole church metrics">
      ${decisionMetric('Published sections', metrics.totalSections, `${metrics.totalDoors.toLocaleString()} doors`)}
      ${decisionMetric('Still available', metrics.unassignedSections, `${metrics.unassignedDoors.toLocaleString()} doors`)}
      ${decisionMetric('Reserved for groups', reserved.length, `${sumDoors(reserved).toLocaleString()} doors`)}
      ${decisionMetric('With people', withPeople.length, `${sumDoors(withPeople).toLocaleString()} doors`)}
      ${decisionMetric('Completed', metrics.completedSections, `${metrics.completedDoors.toLocaleString()} of ${metrics.totalDoors.toLocaleString()} doors · ${completion.toFixed(1)}% of published doors`)}
      ${decisionMetric('People participating', participantNamesForSections(model.dashboard.sections).length, 'Unique names across assignees and entered team members')}
    </section>`;
  }
  if (view === 'NEIGHBORHOOD') {
    return `<section class="decision-metric-grid" aria-label="Neighborhood metrics">
      ${decisionMetric('Published sections', metrics.totalSections, `${metrics.totalDoors.toLocaleString()} residential doors`)}
      ${decisionMetric('Still available', metrics.unassignedSections, `${metrics.unassignedDoors.toLocaleString()} doors`)}
      ${decisionMetric('Currently assigned', metrics.assignedSections, `${metrics.assignedDoors.toLocaleString()} doors, including group reservations`)}
      ${decisionMetric('Completed', metrics.completedSections, `${metrics.completedDoors.toLocaleString()} doors`)}
      ${decisionMetric('Doors completed', `${completion.toFixed(1)}%`, `${metrics.completedDoors.toLocaleString()} of ${metrics.totalDoors.toLocaleString()}`)}
      ${decisionMetric('Observation coverage', selectedSignal ? `${percentage(selectedSignal.observedHouses, selectedSignal.publishedDoors).toFixed(1)}%` : '—', selectedSignal ? `${selectedSignal.observedHouses.toLocaleString()} houses observed from completed work or field reports` : 'No field data')}
    </section>`;
  }
  if (view === 'GROUP') {
    const scoped = selectedEntity ? sectionsForEntity(model.dashboard.sections, selectedEntity) : [];
    const reserved = groupReservedSections(scoped);
    const active = scoped.filter((section) => section.status === 'ASSIGNED' && Boolean(section.assignedTo));
    return `<section class="decision-metric-grid" aria-label="Small group metrics">
      ${decisionMetric('Territory connected', metrics.totalSections, `${metrics.totalDoors.toLocaleString()} doors`)}
      ${decisionMetric('Reserved to hand out', reserved.length, `${sumDoors(reserved).toLocaleString()} doors`)}
      ${decisionMetric('Currently assigned', active.length, `${sumDoors(active).toLocaleString()} doors`)}
      ${decisionMetric('Completed', metrics.completedSections, `${metrics.completedDoors.toLocaleString()} doors`)}
      ${decisionMetric('Doors completed', `${completion.toFixed(1)}%`, `${metrics.completedDoors.toLocaleString()} of ${metrics.totalDoors.toLocaleString()} doors in this group`)}
      ${decisionMetric('People participating', participantNamesForSections(scoped).length, 'Unique assigned people and entered team members')}
    </section>`;
  }

  const primary = selectedEntity ? primaryAssignedSectionsForPerson(model, selectedEntity) : [];
  const unfinished = primary.filter((section) => section.status !== 'COMPLETE');
  const completed = primary.filter((section) => section.status === 'COMPLETE');
  const primaryDoors = sumDoors(primary);
  const primaryCompletedCredit = completed.reduce((sum, section) => sum + sectionDoorCreditForPerson(section, selectedEntity?.id ?? '', selectedEntity?.label ?? ''), 0);
  const primaryCompletion = primary.length ? completed.length / primary.length * 100 : 0;
  return `<section class="decision-metric-grid" aria-label="Person metrics">
    ${decisionMetric('Sections participated', metrics.totalSections, `${formatDoorCredits(metrics.totalDoors)} door-equivalents across participation`)}
    ${decisionMetric('Primary assignments', primary.length, `${primaryDoors.toLocaleString()} full map doors assigned`)}
    ${decisionMetric('Current unfinished', unfinished.length, `${sumDoors(unfinished).toLocaleString()} full map doors still open`)}
    ${decisionMetric('Completed door credit', formatDoorCredits(metrics.completedDoors), 'Each completed section is divided evenly across its recorded team')}
    ${decisionMetric('Primary map completion', `${completed.length} of ${primary.length} maps · ${primaryCompletion.toFixed(1)}%`, primary.length ? `${completed.length} of ${primary.length} maps · ${formatDoorCredits(primaryCompletedCredit)} personal door credit` : 'No direct assignments yet')}
    ${decisionMetric('Team-only sections', Math.max(0, metrics.totalSections - primary.length), 'Sections where this person was entered as going with someone else')}
  </section>`;
}

function decisionMetric(label: string, value: string | number, detail: string): string {
  return `<article class="decision-metric-card"><span>${escapeHtml(label)}</span><strong>${typeof value === 'number' ? value.toLocaleString() : escapeHtml(value)}</strong><small>${escapeHtml(detail)}</small></article>`;
}

function needsAttentionTemplate(
  view: AnalyticsView,
  metrics: AdminCoverageMetrics,
  selectedEntity: AnalyticsEntity | undefined,
  selectedSignal: NeighborhoodSignal | undefined,
  model: AnalyticsModel
): string {
  const items: Array<{ title: string; copy: string; level: 'HIGH' | 'MEDIUM' | 'GOOD' }> = [];
  if (view === 'CHURCH') {
    const reserved = groupReservedSections(model.dashboard.sections);
    const withPeople = model.dashboard.sections.filter((section) => section.status === 'ASSIGNED' && Boolean(section.assignedTo));
    items.push(
      metrics.unassignedSections
        ? { title: `${metrics.unassignedSections} sections still need someone`, copy: 'Prioritize the largest connected unassigned pockets before publishing more territory.', level: 'HIGH' }
        : { title: 'Every published section has an owner or reservation', copy: 'Focus attention on unfinished assignments.', level: 'GOOD' },
      reserved.length
        ? { title: `${reserved.length} sections are reserved for groups`, copy: 'These sections are intentionally off the public pool but have not yet been handed to a person.', level: 'MEDIUM' }
        : { title: 'No group reservations are waiting', copy: 'No group-held territory is waiting to be handed out.', level: 'GOOD' },
      withPeople.length
        ? { title: `${withPeople.length} sections are currently with people`, copy: 'Follow up on older assignments before adding more maps.', level: 'MEDIUM' }
        : { title: 'No person assignments are waiting', copy: 'The current season has no unfinished person assignments.', level: 'GOOD' }
    );
  } else if (view === 'NEIGHBORHOOD' && selectedEntity && selectedSignal) {
    const welcoming = welcomingIndicator(selectedSignal);
    items.push(
      metrics.unassignedSections
        ? { title: `${metrics.unassignedSections} sections remain available`, copy: 'Assign these before creating more sections in this neighborhood.', level: 'HIGH' }
        : { title: 'All published sections are assigned, reserved, or complete', copy: 'No additional distribution is needed right now.', level: 'GOOD' },
      selectedSignal.observedHouses
        ? { title: `${selectedSignal.noSoliciting} No Soliciting reports`, copy: `Current welcoming indicator: ${welcoming.toFixed(1)}% of observed houses had no reported sign.`, level: welcoming < 94 ? 'MEDIUM' : 'GOOD' }
        : { title: 'No house-level observations yet', copy: 'Observation data appears from completed sections and field condition reports.', level: 'GOOD' },
      { title: `${selectedSignal.emptyLots + selectedSignal.underConstruction} property conditions`, copy: 'These counts come from actual field reports stored on published route points.', level: selectedSignal.emptyLots + selectedSignal.underConstruction ? 'MEDIUM' : 'GOOD' }
    );
  } else if (view === 'GROUP' && selectedEntity) {
    const scoped = sectionsForEntity(model.dashboard.sections, selectedEntity);
    const reserved = groupReservedSections(scoped);
    const active = scoped.filter((section) => section.status === 'ASSIGNED' && Boolean(section.assignedTo));
    items.push(
      reserved.length
        ? { title: `${reserved.length} reserved section${reserved.length === 1 ? '' : 's'} have not been handed out`, copy: 'The group leader should assign these before receiving more territory.', level: 'HIGH' }
        : { title: 'All reserved territory has been handed out', copy: 'The next step is follow-up and completion.', level: 'GOOD' },
      active.length
        ? { title: `${active.length} group section${active.length === 1 ? '' : 's'} are unfinished`, copy: 'See whether one member is carrying too much of the group’s work.', level: 'MEDIUM' }
        : { title: 'No unfinished group assignments', copy: 'This group is ready for additional territory when needed.', level: 'GOOD' }
    );
  } else if (view === 'PERSON' && selectedEntity) {
    const unfinished = unfinishedAssignedSectionsForPerson(model, selectedEntity);
    const completed = primaryAssignedSectionsForPerson(model, selectedEntity).filter((section) => section.status === 'COMPLETE');
    items.push(
      unfinished.length
        ? { title: `${unfinished.length} unfinished assigned map${unfinished.length === 1 ? '' : 's'}`, copy: 'Use Print maps beside the person’s name to print only those open sections.', level: 'HIGH' }
        : { title: 'No unfinished assigned maps', copy: 'This person has no direct assignment waiting for completion.', level: 'GOOD' },
      completed.length
        ? { title: `${completed.length} primary section${completed.length === 1 ? '' : 's'} completed`, copy: 'Completed work remains in live analytics until test activity is cleared.', level: 'GOOD' }
        : { title: 'No primary sections completed', copy: 'Team-only participation does not count as a direct assignment.', level: 'MEDIUM' }
    );
  }
  return `<section class="analytics-card decision-attention-card"><div class="decision-section-heading"><div><p class="eyebrow">Decision support</p><h2>Needs attention</h2></div><span>Only live, actionable signals are shown</span></div><div class="decision-attention-grid">${items.map((item) => `<article class="decision-attention-item ${item.level.toLowerCase()}"><i aria-hidden="true"></i><div><strong>${escapeHtml(item.title)}</strong><p>${escapeHtml(item.copy)}</p></div></article>`).join('')}</div></section>`;
}

function decisionPanelTemplate(
  view: AnalyticsView,
  metrics: AdminCoverageMetrics,
  selectedEntity: AnalyticsEntity | undefined,
  selectedSignal: NeighborhoodSignal | undefined,
  _signals: NeighborhoodSignal[],
  model: AnalyticsModel
): string {
  if (view === 'CHURCH') {
    const lowest = lowestCompletionNeighborhoods(model, 5);
    const participants = participantNamesForSections(model.dashboard.sections);
    return `<aside class="analytics-card decision-side-panel"><div class="decision-section-heading compact"><div><p class="eyebrow">Where to look next</p><h2>Church-wide priorities</h2></div></div><div class="decision-priority-list">${lowest.length ? lowest.map((item, index) => `<button type="button" data-analytics-jump-view="NEIGHBORHOOD" data-analytics-jump-id="${escapeHtml(item.entity.id)}"><span>${index + 1}</span><div><strong>${escapeHtml(item.entity.label)}</strong><small>${item.completedDoors.toLocaleString()} of ${item.totalDoors.toLocaleString()} doors · ${item.percent.toFixed(1)}% complete</small></div></button>`).join('') : '<p>No published neighborhoods yet.</p>'}</div><details class="decision-details"><summary>Show complete church breakdown</summary><div class="decision-details-body"><p>${metrics.totalSections.toLocaleString()} published sections across ${model.neighborhoods.length.toLocaleString()} neighborhoods.</p><p>${metrics.unassignedDoors.toLocaleString()} doors remain generally available and ${metrics.completedDoors.toLocaleString()} doors are complete.</p></div></details><details class="decision-details"><summary>Show participating names (${participants.length.toLocaleString()})</summary><div class="decision-details-body participant-name-list">${participants.length ? participants.map((name) => `<div class="decision-list-row"><span>${escapeHtml(name)}</span></div>`).join('') : '<p>No assigned people or entered team members are recorded yet.</p>'}</div></details></aside>`;
  }
  if (view === 'NEIGHBORHOOD' && selectedEntity && selectedSignal) {
    const welcoming = welcomingIndicator(selectedSignal);
    return `<aside class="analytics-card decision-side-panel"><div class="decision-section-heading compact"><div><p class="eyebrow">Neighborhood signal</p><h2>Welcoming indicator</h2></div><strong class="decision-welcoming-score">${selectedSignal.observedHouses ? `${welcoming.toFixed(1)}%` : '—'}</strong></div><p class="decision-definition">Percentage of observed residential houses without a reported No Soliciting sign. This describes access conditions—not the character of residents.</p><div class="decision-condition-grid"><div><strong>${selectedSignal.noSoliciting.toLocaleString()}</strong><span>No Soliciting</span></div><div><strong>${selectedSignal.observedHouses.toLocaleString()}</strong><span>houses observed</span></div><div><strong>${selectedSignal.emptyLots.toLocaleString()}</strong><span>empty lots</span></div><div><strong>${selectedSignal.underConstruction.toLocaleString()}</strong><span>construction</span></div></div><details class="decision-details"><summary>How observation is counted</summary><div class="decision-details-body"><p>A house is counted as observed when its published section is complete or when a field condition was explicitly reported for that address. The counts come from live route data; no synthetic neighborhood values are used.</p></div></details></aside>`;
  }
  if (view === 'GROUP' && selectedEntity) {
    const scoped = sectionsForEntity(model.dashboard.sections, selectedEntity);
    const participants = participantNamesForSections(scoped);
    const reserved = groupReservedSections(scoped);
    return `<aside class="analytics-card decision-side-panel"><div class="decision-section-heading compact"><div><p class="eyebrow">Leadership question</p><h2>What should happen next?</h2></div></div><div class="decision-callout">${reserved.length ? `<strong>Hand out the ${reserved.length} reserved section${reserved.length === 1 ? '' : 's'} before adding more territory.</strong>` : '<strong>This group has distributed all currently reserved territory.</strong>'}<p>Use the map to see whether unfinished work is concentrated in one area or one person.</p></div><details class="decision-details"><summary>Show participating people (${participants.length.toLocaleString()})</summary><div class="decision-details-body participant-name-list">${participants.length ? participants.map((name) => `<div class="decision-list-row"><span>${escapeHtml(name)}</span></div>`).join('') : '<p>No assigned people or entered team members are connected to this group yet.</p>'}</div></details><details class="decision-details"><summary>Show section totals</summary><div class="decision-details-body"><p>${metrics.completedSections} completed · ${scoped.filter((section) => section.status === 'ASSIGNED' && section.assignedTo).length} with people · ${reserved.length} reserved to hand out.</p></div></details></aside>`;
  }
  if (view === 'PERSON' && selectedEntity) {
    const primary = primaryAssignedSectionsForPerson(model, selectedEntity);
    const unfinished = primary.filter((section) => section.status !== 'COMPLETE');
    const timeline = personTimeline(primary);
    const groupNames = uniqueStrings(primary.flatMap((section) => section.groupName ? [section.groupName] : []));
    return `<aside class="analytics-card decision-side-panel"><div class="decision-section-heading compact"><div><p class="eyebrow">Pastoral follow-up</p><h2>Assignment summary</h2></div></div><div class="decision-callout">${unfinished.length ? `<strong>Follow up before assigning another map.</strong><p>${unfinished.length} unfinished section${unfinished.length === 1 ? '' : 's'} are directly assigned to ${escapeHtml(selectedEntity.label)}.</p>` : `<strong>No unfinished direct assignments.</strong><p>${escapeHtml(selectedEntity.label)} has no open primary map in the live data.</p>`}</div><details class="decision-details"><summary>Show assignment timeline</summary>${timeline.length ? `<ol class="decision-timeline">${timeline.map((item) => `<li><time>${escapeHtml(formatDate(item.date))}</time><span>${escapeHtml(item.label)}</span></li>`).join('')}</ol>` : '<div class="decision-details-body"><p>No direct assignment timestamps are recorded.</p></div>'}</details><details class="decision-details"><summary>Show connected small group</summary><div class="decision-details-body"><p>${groupNames.length ? escapeHtml(groupNames.join(', ')) : 'No small group is connected to this person’s direct assignments.'}</p></div></details></aside>`;
  }
  return '<aside class="analytics-card decision-side-panel"><p>Select a scope to see decision-ready details.</p></aside>';
}

function buildAnalyticsPrintReport(model: AnalyticsModel, season: SeasonState): AnalyticsPrintReport {
  const sections = model.dashboard.sections;
  const summaryMetrics = coverageMetrics(sections);
  const signals = neighborhoodSignals(sections);
  const participantNames = participantNamesForSections(sections);
  const creditAudit = auditCompletedSectionCredits(sections);
  const missingCreditRosters = creditAudit.filter((row) => row.source === 'MISSING_ROSTER').length;
  const fallbackCreditRosters = creditAudit.filter((row) => row.source === 'ASSIGNEE_FALLBACK').length;

  const neighborhoods = model.neighborhoods.map((entity) => {
    const scoped = sectionsForEntity(sections, entity);
    const metrics = coverageMetrics(scoped);
    const signal = signals.find((item) => item.neighborhoodId === entity.id);
    return {
      name: entity.label,
      totalSections: metrics.totalSections,
      unassignedSections: metrics.unassignedSections,
      assignedSections: metrics.assignedSections,
      completedSections: metrics.completedSections,
      totalDoors: metrics.totalDoors,
      completedDoors: metrics.completedDoors,
      completionPercent: metrics.completionPercent,
      observedHouses: signal?.observedHouses ?? 0,
      observationPercent: signal ? percentage(signal.observedHouses, signal.publishedDoors) : 0,
      noSoliciting: signal?.noSoliciting ?? 0,
      welcomingPercent: signal ? welcomingIndicator(signal) : 0,
      emptyLots: signal?.emptyLots ?? 0,
      underConstruction: signal?.underConstruction ?? 0
    };
  }).sort((left, right) => left.completionPercent - right.completionPercent || left.name.localeCompare(right.name));

  const groups = model.groups.map((entity) => {
    const scoped = sectionsForEntity(sections, entity);
    const metrics = coverageMetrics(scoped);
    const reserved = groupReservedSections(scoped);
    const active = scoped.filter((section) => section.status === 'ASSIGNED' && Boolean(section.assignedTo));
    return {
      name: entity.label,
      totalSections: metrics.totalSections,
      reservedSections: reserved.length,
      assignedSections: active.length,
      completedSections: metrics.completedSections,
      totalDoors: metrics.totalDoors,
      completedDoors: metrics.completedDoors,
      completionPercent: metrics.completionPercent,
      participants: participantNamesForSections(scoped).length
    };
  }).filter((row) => row.totalSections > 0).sort((left, right) => left.completionPercent - right.completionPercent || left.name.localeCompare(right.name));

  const people = model.people.map((entity) => {
    const participation = sectionsForEntity(sections, entity);
    const primary = primaryAssignedSectionsForPerson(model, entity);
    const primaryMetrics = coverageMetrics(primary);
    const unfinished = primary.filter((section) => section.status !== 'COMPLETE');
    return {
      name: entity.label,
      groupName: uniqueStrings(primary.flatMap((section) => section.groupName ? [section.groupName] : [])).join(', '),
      connectedSections: participation.length,
      unfinishedSections: unfinished.length,
      completedSections: participation.filter((section) => section.status === 'COMPLETE').length,
      assignedDoors: primaryMetrics.totalDoors,
      completedDoors: participation.reduce((sum, section) => sum + sectionDoorCreditForPerson(section, entity.id, entity.label), 0),
      completionPercent: participation.reduce((sum, section) => sum + sectionDoorCreditForPerson(section, entity.id, entity.label), 0)
        ? Math.round(participation.filter((section) => section.status === 'COMPLETE').length / Math.max(1, participation.length) * 1000) / 10
        : primaryMetrics.completionPercent
    };
  }).filter((row) => row.connectedSections > 0).sort((left, right) => left.name.localeCompare(right.name));

  const belowHalf = neighborhoods.filter((row) => row.completionPercent < 50).length;
  const unassignedGroups = groups.filter((row) => row.reservedSections > 0).length;
  const unfinishedPeople = people.filter((row) => row.unfinishedSections > 0).length;
  const attentionItems = [
    summaryMetrics.unassignedSections
      ? `${summaryMetrics.unassignedSections} published sections (${summaryMetrics.unassignedDoors.toLocaleString()} doors) still need an owner or group reservation.`
      : 'Every published section has an owner, reservation, or completion record.',
    groupReservedSections(sections).length
      ? `${groupReservedSections(sections).length} sections are reserved for small groups and not yet assigned to a person.`
      : 'No group reservations are waiting to be handed out.',
    sections.filter((section) => section.status === 'ASSIGNED' && section.assignedTo).length
      ? `${sections.filter((section) => section.status === 'ASSIGNED' && section.assignedTo).length} sections are assigned to people but not yet complete.`
      : 'No person-assigned sections are waiting for completion.',
    belowHalf
      ? `${belowHalf} neighborhoods are below 50% door completion.`
      : 'Every published neighborhood is at least 50% complete.',
    unassignedGroups
      ? `${unassignedGroups} small groups still have reserved sections to hand out.`
      : 'No small group has reserved sections waiting to be handed out.',
    missingCreditRosters
      ? `${missingCreditRosters} completed sections have no recorded participant or direct assignee, so they need roster review before personal credit can be assigned.`
      : fallbackCreditRosters
        ? `${fallbackCreditRosters} completed sections have no saved team roster; the direct assignee receives provisional solo credit and should be verified.`
        : 'Every completed section has a recorded team roster for personal credit.',
    unfinishedPeople
      ? `${unfinishedPeople} people have at least one unfinished direct assignment.`
      : 'No person has an unfinished direct assignment.'
  ];

  return {
    title: 'Coverage Analytics Report',
    seasonLabel: season.name,
    seasonStatus: season.status === 'CLOSED' ? 'CLOSED' : 'ACTIVE',
    seasonNote: `Live ${season.status.toLowerCase()} season data. Assignment and completion activity is read from the same records used by the admin, leader, and volunteer pages.`,
    generatedAt: new Date().toISOString(),
    summary: {
      ...summaryMetrics,
      peopleParticipating: participantNames.length
    },
    attentionItems,
    neighborhoods,
    groups,
    people,
    participantNames,
    sections,
    creditAudit
  };
}

function wireAnalyticsPage(
  root: HTMLDivElement,
  state: AnalyticsState,
  render: () => void,
  signOut: () => void,
  actions: { printReport: () => void; printPersonMaps: () => void; refresh: () => void }
): void {
  wireHeaderSignOut(root, signOut);
  root.querySelector<HTMLButtonElement>('#analytics-print-report')?.addEventListener('click', actions.printReport);
  root.querySelector<HTMLButtonElement>('#analytics-print-person-maps')?.addEventListener('click', actions.printPersonMaps);
  root.querySelector<HTMLButtonElement>('#analytics-refresh')?.addEventListener('click', actions.refresh);

  root.querySelectorAll<HTMLButtonElement>('[data-analytics-view]').forEach((button) => {
    button.addEventListener('click', () => {
      state.view = button.dataset.analyticsView as AnalyticsView;
      state.selectedId = '';
      state.query = '';
      render();
    });
  });

  const search = root.querySelector<HTMLInputElement>('#analytics-entity-search');
  search?.addEventListener('input', () => {
    state.query = search.value;
    render();
    window.requestAnimationFrame(() => {
      const next = root.querySelector<HTMLInputElement>('#analytics-entity-search');
      next?.focus();
      next?.setSelectionRange(next.value.length, next.value.length);
    });
  });
  search?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      state.query = '';
      render();
    }
  });

  root.querySelector<HTMLButtonElement>('#analytics-clear-entity')?.addEventListener('click', () => {
    state.selectedId = '';
    state.query = '';
    render();
  });

  root.querySelectorAll<HTMLButtonElement>('[data-analytics-entity-id]').forEach((button) => {
    button.addEventListener('click', () => {
      state.selectedId = button.dataset.analyticsEntityId ?? '';
      state.query = '';
      render();
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-analytics-jump-view]').forEach((button) => {
    button.addEventListener('click', () => {
      state.view = button.dataset.analyticsJumpView as AnalyticsView;
      state.selectedId = button.dataset.analyticsJumpId ?? '';
      state.query = '';
      render();
    });
  });
}

function wireHeaderSignOut(root: HTMLDivElement, signOut: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-analytics-sign-out]').forEach((button) => {
    button.addEventListener('click', signOut);
  });
}

function buildAnalyticsModel(dashboard: AdminCoverageDashboard): AnalyticsModel {
  const sectionsByNeighborhood = new Map<string, AdminCoverageSection[]>();
  for (const section of dashboard.sections) {
    const list = sectionsByNeighborhood.get(section.neighborhoodId) ?? [];
    list.push(section);
    sectionsByNeighborhood.set(section.neighborhoodId, list);
  }
  const neighborhoods = Array.from(sectionsByNeighborhood.entries())
    .map(([id, sections]) => ({
      id,
      label: sections[0]?.neighborhoodName ?? id,
      subtitle: `${sections.length.toLocaleString()} published sections`,
      sectionIds: sections.map((section) => section.id)
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const groupMap = new Map<string, AnalyticsEntity>();
  const people: AnalyticsEntity[] = [];
  for (const section of dashboard.sections) {
    if (section.groupId || section.groupName) {
      const id = section.groupId ?? `GROUP-${slug(section.groupName ?? 'Unknown')}`;
      const current = groupMap.get(id) ?? {
        id,
        label: section.groupName ?? 'Unnamed small group',
        subtitle: 'Small group',
        sectionIds: []
      };
      if (!current.sectionIds.includes(section.id)) current.sectionIds.push(section.id);
      groupMap.set(id, current);
    }

    if (section.assignedTo) {
      people.push({
        id: section.assignedTo.id || `PERSON-${slug(section.assignedTo.displayName)}`,
        label: section.assignedTo.displayName,
        subtitle: 'Person · direct assignment',
        sectionIds: [section.id],
        assignedSectionIds: [section.id],
        ...(section.groupId ? { groupId: section.groupId } : {})
      });
    }
    for (const member of section.teamMembers) {
      people.push({
        id: member.personId ?? `PERSON-${slug(member.name)}`,
        label: member.name,
        subtitle: 'Person · participant',
        sectionIds: [section.id],
        assignedSectionIds: [],
        ...(section.groupId ? { groupId: section.groupId } : {})
      });
    }
  }

  return {
    dashboard,
    neighborhoods,
    groups: Array.from(groupMap.values()).sort((left, right) => left.label.localeCompare(right.label)),
    people: dedupePeople(people).sort((left, right) => left.label.localeCompare(right.label))
  };
}

function dedupePeople(entities: AnalyticsEntity[]): AnalyticsEntity[] {
  const byIdentity = new Map<string, AnalyticsEntity>();
  for (const entity of entities) {
    const key = entity.id.startsWith('PERSON-') ? `name:${normalize(entity.label)}` : `id:${entity.id}`;
    if (!key) continue;
    const current = byIdentity.get(key);
    if (!current) {
      byIdentity.set(key, {
        ...entity,
        sectionIds: [...entity.sectionIds],
        assignedSectionIds: [...(entity.assignedSectionIds ?? [])]
      });
      continue;
    }
    for (const sectionId of entity.sectionIds) {
      if (!current.sectionIds.includes(sectionId)) current.sectionIds.push(sectionId);
    }
    current.assignedSectionIds ??= [];
    for (const sectionId of entity.assignedSectionIds ?? []) {
      if (!current.assignedSectionIds.includes(sectionId)) current.assignedSectionIds.push(sectionId);
    }
    if (!current.groupId && entity.groupId) current.groupId = entity.groupId;
    if ((current.assignedSectionIds?.length ?? 0) > 0) current.subtitle = 'Person · direct assignment';
  }
  return Array.from(byIdentity.values());
}

function sectionsForView(
  sections: AdminCoverageSection[],
  view: AnalyticsView,
  selectedEntity: AnalyticsEntity | undefined
): AdminCoverageSection[] {
  if (view === 'CHURCH') return sections;
  if (!selectedEntity) return [];
  return sectionsForEntity(sections, selectedEntity);
}

function sectionsForEntity(sections: AdminCoverageSection[], entity: AnalyticsEntity): AdminCoverageSection[] {
  const selected = new Set(entity.sectionIds);
  return sections.filter((section) => selected.has(section.id));
}

function findSelectedEntity(model: AnalyticsModel, state: AnalyticsState): AnalyticsEntity | undefined {
  return entitiesForView(model, state.view).find((item) => item.id === state.selectedId);
}

function entitiesForView(model: AnalyticsModel, view: AnalyticsView): AnalyticsEntity[] {
  if (view === 'NEIGHBORHOOD') return model.neighborhoods;
  if (view === 'GROUP') return model.groups;
  if (view === 'PERSON') return model.people;
  return [];
}

function searchEntities(model: AnalyticsModel, view: AnalyticsView, query: string): AnalyticsEntity[] {
  const normalized = normalize(query);
  if (!normalized) return [];
  return entitiesForView(model, view)
    .filter((item) => normalize(`${item.label} ${item.subtitle}`).includes(normalized))
    .slice(0, 10);
}

function coverageMetrics(sections: AdminCoverageSection[]): AdminCoverageMetrics {
  const metrics: AdminCoverageMetrics = {
    totalSections: sections.length,
    totalDoors: 0,
    unassignedSections: 0,
    unassignedDoors: 0,
    assignedSections: 0,
    assignedDoors: 0,
    completedSections: 0,
    completedDoors: 0,
    completionPercent: 0
  };
  for (const section of sections) {
    metrics.totalDoors += section.doors;
    if (section.status === 'COMPLETE') {
      metrics.completedSections += 1;
      metrics.completedDoors += section.doors;
    } else if (section.status === 'ASSIGNED') {
      metrics.assignedSections += 1;
      metrics.assignedDoors += section.doors;
    } else {
      metrics.unassignedSections += 1;
      metrics.unassignedDoors += section.doors;
    }
  }
  metrics.completionPercent = metrics.totalDoors
    ? Math.round(metrics.completedDoors / metrics.totalDoors * 1000) / 10
    : 0;
  return metrics;
}

function personCoverageMetrics(sections: AdminCoverageSection[], person: AnalyticsEntity): AdminCoverageMetrics {
  const metrics: AdminCoverageMetrics = {
    totalSections: 0,
    totalDoors: 0,
    unassignedSections: 0,
    unassignedDoors: 0,
    assignedSections: 0,
    assignedDoors: 0,
    completedSections: 0,
    completedDoors: 0,
    completionPercent: 0
  };
  for (const section of sections) {
    const credit = sectionDoorCreditForPerson(section, person.id, person.label);
    if (!credit) continue;
    metrics.totalSections += 1;
    metrics.totalDoors += credit;
    if (section.status === 'COMPLETE') {
      metrics.completedSections += 1;
      metrics.completedDoors += credit;
    } else if (section.status === 'ASSIGNED') {
      metrics.assignedSections += 1;
      metrics.assignedDoors += credit;
    } else {
      metrics.unassignedSections += 1;
      metrics.unassignedDoors += credit;
    }
  }
  metrics.completionPercent = metrics.totalDoors
    ? Math.round(metrics.completedDoors / metrics.totalDoors * 1000) / 10
    : 0;
  return metrics;
}

function formatDoorCredits(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

function neighborhoodSignals(sections: AdminCoverageSection[]): NeighborhoodSignal[] {
  const byNeighborhood = new Map<string, AdminCoverageSection[]>();
  for (const section of sections) {
    const list = byNeighborhood.get(section.neighborhoodId) ?? [];
    list.push(section);
    byNeighborhood.set(section.neighborhoodId, list);
  }

  return Array.from(byNeighborhood.entries()).map(([neighborhoodId, scoped]) => {
    const publishedDoors = sumDoors(scoped);
    return {
      neighborhoodId,
      neighborhoodName: scoped[0]?.neighborhoodName ?? neighborhoodId,
      publishedDoors,
      observedHouses: Math.min(publishedDoors, scoped.reduce((sum, section) => sum + (section.fieldObservations?.observedHouses ?? 0), 0)),
      noSoliciting: scoped.reduce((sum, section) => sum + (section.fieldObservations?.noSoliciting ?? 0), 0),
      emptyLots: scoped.reduce((sum, section) => sum + (section.fieldObservations?.emptyLots ?? 0), 0),
      underConstruction: scoped.reduce((sum, section) => sum + (section.fieldObservations?.underConstruction ?? 0), 0)
    };
  }).sort((left, right) => left.neighborhoodName.localeCompare(right.neighborhoodName));
}

function welcomingIndicator(signal: NeighborhoodSignal): number {
  return signal.observedHouses
    ? Math.max(0, 100 - signal.noSoliciting / signal.observedHouses * 100)
    : 0;
}

function participantNamesForSections(sections: AdminCoverageSection[]): string[] {
  const uniqueByName = new Map<string, string>();
  const addParticipant = (rawName: string | undefined): void => {
    const name = String(rawName ?? '').trim().replace(/\s+/g, ' ');
    const normalizedName = normalize(name);
    if (!normalizedName || uniqueByName.has(normalizedName)) return;
    uniqueByName.set(normalizedName, name);
  };
  for (const section of sections) {
    if (section.assignedTo) addParticipant(section.assignedTo.displayName);
    for (const member of section.teamMembers) addParticipant(member.name);
  }
  return [...uniqueByName.values()].sort((left, right) => left.localeCompare(right));
}

function groupReservedSections(sections: AdminCoverageSection[]): AdminCoverageSection[] {
  return sections.filter((section) => section.status !== 'COMPLETE' && section.reservedForGroup === true && !section.assignedTo);
}

function primaryAssignedSectionsForPerson(model: AnalyticsModel, person: AnalyticsEntity): AdminCoverageSection[] {
  const ids = new Set(person.assignedSectionIds ?? []);
  return model.dashboard.sections.filter((section) => ids.has(section.id) && section.assignedTo && samePerson(section.assignedTo.id, section.assignedTo.displayName, person));
}

function unfinishedAssignedSectionsForPerson(model: AnalyticsModel, person: AnalyticsEntity): AdminCoverageSection[] {
  return primaryAssignedSectionsForPerson(model, person)
    .filter((section) => section.status !== 'COMPLETE')
    .sort((left, right) => left.neighborhoodName.localeCompare(right.neighborhoodName) || left.sectionNumber - right.sectionNumber);
}

function samePerson(id: string, displayName: string, person: AnalyticsEntity): boolean {
  return Boolean(id && id === person.id) || normalize(displayName) === normalize(person.label);
}

function lowestCompletionNeighborhoods(
  model: AnalyticsModel,
  count: number
): Array<{ entity: AnalyticsEntity; percent: number; completedDoors: number; totalDoors: number }> {
  return model.neighborhoods.map((entity) => {
    const metrics = coverageMetrics(sectionsForEntity(model.dashboard.sections, entity));
    return { entity, percent: metrics.completionPercent, completedDoors: metrics.completedDoors, totalDoors: metrics.totalDoors };
  }).sort((left, right) => left.percent - right.percent).slice(0, count);
}

function personTimeline(sections: AdminCoverageSection[]): Array<{ date: string; label: string }> {
  const events: Array<{ date: string; label: string }> = [];
  for (const section of sections) {
    const prefix = `${section.neighborhoodName} · Section ${section.sectionNumber}`;
    if (section.assignedAt) events.push({ date: section.assignedAt, label: `${prefix} assigned` });
    if (section.startedAt) events.push({ date: section.startedAt, label: `${prefix} started` });
    if (section.completedAt) events.push({ date: section.completedAt, label: `${prefix} completed` });
  }
  return events.sort((left, right) => right.date.localeCompare(left.date)).slice(0, 8);
}

function sumDoors(sections: AdminCoverageSection[]): number {
  return sections.reduce((sum, section) => sum + section.doors, 0);
}

function uniqueStrings(values: string[]): string[] {
  const result = new Map<string, string>();
  for (const value of values) {
    const clean = value.trim().replace(/\s+/g, ' ');
    const key = normalize(clean);
    if (key && !result.has(key)) result.set(key, clean);
  }
  return [...result.values()];
}

function slug(value: string): string {
  return normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').toUpperCase() || 'UNKNOWN';
}

function percentage(numerator: number, denominator: number): number {
  return denominator ? numerator / denominator * 100 : 0;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function formatRelativeSync(value: string): string {
  if (!value) return 'just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'just now';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  return formatDate(value);
}

function normalize(value: string): string {
  return value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character] ?? character));
}
